import { FLIGHT_DIRECTION } from "@mozioinc/query";
import {
  Button,
  CountryInput,
  FloatingInput,
  FloatingInputGroup,
  FloatingInputClearButton,
  FloatingLabel,
  Loading,
  PhoneInput,
  cn,
} from "@mozioinc/ui";
import { PaymentElement } from "@stripe/react-stripe-js";
import { Link } from "@tanstack/react-router";
import { useFormContext, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { FlightCombobox } from "../containers/flight-combobox";
import { ReactComponent as ReasonSvg } from "./assets/reason.svg";
import { ReactComponent as TravelerSvg } from "./assets/traveler.svg";
import { PriceSummary } from "./price-summary";
import { PurchaseSummary } from "./purchase-summary";
import { Select } from "./select";
import type { TransNamespace } from "../types";
import type { ParkingSearch, ParkingPricingResult } from "@mozioinc/query";

const flightSchema = z.object({
  airlineCode: z.string().min(1),
  flightCode: z.string().min(1),
  originTerminal: z.string().min(1).optional(),
  destinationTerminal: z.string().min(1).optional(),
});

export const formSchema = z
  .object({
    vehicle: z.object({
      registrationNumber: z.string().min(1),
      make: z.string().min(1),
      model: z.string().min(1),
      color: z.string().min(1),
    }),
    customer: z.object({
      firstName: z.string().min(1),
      lastName: z.string().min(1),
      email: z.string().email().min(1),
      confirmEmail: z.string().min(1),
      phone: z.string().min(1),
      countryCode: z.object({
        name: z.string().min(1),
        callingCode: z.string().min(1),
        code: z.string().min(1),
      }),
    }),
    departureFlight: flightSchema,
    arrivalFlight: flightSchema,
    passengerCount: z.string().optional(),
    travelReason: z.string().optional(),
    couponCode: z.string().optional(),
  })
  .refine((data) => data.customer.email === data.customer.confirmEmail, {
    message: "Email and confirmEmail must match",
    path: ["customer", "confirmEmail"],
  });

export type FormValues = z.infer<typeof formSchema>;

type BookingFormProps = {
  onSubmit: () => void;
  searchResult: ParkingSearch;
  pricingResult: ParkingPricingResult | null;
  onCouponCodeChange?: (code: string) => void;
  loading: boolean;
};

const BookingForm = ({
  onSubmit,
  searchResult,
  pricingResult,
  onCouponCodeChange,
  loading,
}: BookingFormProps) => {
  const { t } = useTranslation<TransNamespace>("parking:booking-form");
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const { results, ...bookingData } = searchResult;
  const parkingDetails = results?.[0];

  const firstNameField = useController({
    name: "customer.firstName",
    control,
  });

  const lastNameField = useController({
    name: "customer.lastName",
    control,
  });

  const emailField = useController({
    name: "customer.email",
    control,
  });

  const confirmEmailField = useController({
    name: "customer.confirmEmail",
    control,
  });

  const countryCodeField = useController({
    name: "customer.countryCode",
    control,
  });

  const phoneField = useController({
    name: "customer.phone",
    control,
  });

  // const ageField = useController({
  //   name: "customer.age",
  //   control,
  // });

  const departureFlightField = useController({
    name: "departureFlight",
    control,
  });

  const arrivalFlightField = useController({
    name: "arrivalFlight",
    control,
  });

  const vehicleRegistrationNumberField = useController({
    name: "vehicle.registrationNumber",
    control,
  });

  const vehicleMakeField = useController({
    name: "vehicle.make",
    control,
  });

  const vehicleModelField = useController({
    name: "vehicle.model",
    control,
  });

  const vehicleColorField = useController({
    name: "vehicle.color",
    control,
  });

  const passengerCountField = useController({
    name: "passengerCount",
    control,
  });

  const travelReasonField = useController({
    name: "travelReason",
    control,
  });

  const couponCodeField = useController({
    name: "couponCode",
    control,
  });

  const renderPurchaseSummary = () =>
    parkingDetails && (
      <PurchaseSummary
        className="lg:px-0"
        location={`${bookingData.location?.value}: ${bookingData.location?.name}`}
        price={pricingResult?.originalPrice?.display}
        arrivalDateTime={bookingData.arrivalDateTime}
        departureDateTime={bookingData.departureDateTime}
        name={parkingDetails.name}
        airportTransferSummary={parkingDetails.airportTransferSummary}
        features={parkingDetails.features}
        logoUrl={parkingDetails.logoUrl}
      />
    );

  return (
    <>
      <div className="lg:hidden">{renderPurchaseSummary()}</div>
      <form
        onSubmit={onSubmit}
        className="pb-16 lg:mt-6 lg:grid lg:grid-cols-[1.5fr_1fr] lg:items-start lg:gap-5"
      >
        <div className="border-y border-y-neutral-200 bg-white px-3 pb-8 pt-2 lg:mt-3 lg:rounded-lg lg:border lg:border-neutral-200 lg:px-5 lg:py-8">
          <h2 className="mb-2 text-lg font-semibold leading-9 lg:mb-3 lg:text-2xl">
            {t("Driver Details")}
          </h2>

          <div className="mt-2 grid grid-cols-2 items-start gap-3 lg:mt-5 lg:gap-x-5">
            <div>
              <FloatingInputGroup
                error={Boolean(firstNameField.fieldState.error)}
              >
                <FloatingLabel htmlFor="firstName">
                  {t("First Name")} *
                </FloatingLabel>
                <FloatingInput
                  value={firstNameField.field.value}
                  name={firstNameField.field.name}
                  onValueChange={firstNameField.field.onChange}
                  ref={firstNameField.field.ref}
                  id="firstName"
                />
                <FloatingInputClearButton
                  onClick={() => firstNameField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.customer?.firstName && (
                <ErrorMessage>
                  {t("Please fill in your first name")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup
                error={Boolean(lastNameField.fieldState.error)}
              >
                <FloatingLabel htmlFor="lastName">
                  {t("Last Name")} *
                </FloatingLabel>
                <FloatingInput
                  value={lastNameField.field.value}
                  name={lastNameField.field.name}
                  onValueChange={lastNameField.field.onChange}
                  ref={lastNameField.field.ref}
                  id="lastName"
                />
                <FloatingInputClearButton
                  onClick={() => lastNameField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.customer?.lastName && (
                <ErrorMessage>
                  {t("Please fill in your last name")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup error={Boolean(emailField.fieldState.error)}>
                <FloatingLabel htmlFor="email">{t("Email")} *</FloatingLabel>
                <FloatingInput
                  value={emailField.field.value}
                  name={emailField.field.name}
                  onValueChange={emailField.field.onChange}
                  ref={emailField.field.ref}
                  type="email"
                  id="email"
                />
                <FloatingInputClearButton
                  onClick={() => emailField.field.onChange("")}
                />
              </FloatingInputGroup>
              {emailField.fieldState.isTouched && errors.customer?.email && (
                <ErrorMessage>
                  {errors.customer?.email.type === "invalid_string"
                    ? t("Please enter a valid email")
                    : t("Please fill in your email")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup
                error={Boolean(confirmEmailField.fieldState.error)}
              >
                <FloatingLabel htmlFor="emailConfirmation">
                  {t("Confirm Email")} *
                </FloatingLabel>
                <FloatingInput
                  value={confirmEmailField.field.value}
                  name={confirmEmailField.field.name}
                  onValueChange={confirmEmailField.field.onChange}
                  ref={confirmEmailField.field.ref}
                  type="email"
                  id="emailConfirmation"
                />
                <FloatingInputClearButton
                  onClick={() => confirmEmailField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.customer?.confirmEmail && (
                <ErrorMessage>
                  {confirmEmailField.field.value
                    ? t("Please double-check your email")
                    : t("Please confirm your email")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <CountryInput
                error={Boolean(countryCodeField.fieldState.error)}
                value={countryCodeField.field.value}
                name={countryCodeField.field.name}
                onValueChange={countryCodeField.field.onChange}
                inputRef={countryCodeField.field.ref}
                id="country"
                label={t("Country") + " *"}
              />
              {errors.customer?.countryCode && (
                <ErrorMessage>
                  {t("Please fill in your country code")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <PhoneInput
                error={Boolean(phoneField.fieldState.error)}
                callingCode={countryCodeField.field.value?.callingCode}
                value={phoneField.field.value}
                name={phoneField.field.name}
                onValueChange={phoneField.field.onChange}
                ref={phoneField.field.ref}
                id="phone-number"
                label={t("Phone Number") + " *"}
              />
              {errors.customer?.phone && (
                <ErrorMessage>{t("Please fill in your phone")}</ErrorMessage>
              )}
            </div>
          </div>

          <h2 className="mb-2 mt-6 text-lg font-semibold leading-9 lg:mt-9 lg:text-2xl">
            {t("Flight Information")}
          </h2>
          <div className="mt-1 grid grid-cols-1 items-start gap-3 lg:gap-x-5">
            <div>
              <FlightCombobox
                airportCode={searchResult.location.value}
                label={t("Departing Flight") + " *"}
                id="departureFlight"
                direction={FLIGHT_DIRECTION.DEPARTURE}
                name={departureFlightField.field.name}
                onValueChange={departureFlightField.field.onChange}
                error={Boolean(departureFlightField.fieldState.error)}
                dateTime={searchResult.arrivalDateTime}
                inputRef={departureFlightField.field.ref}
              />
              {errors.departureFlight && (
                <ErrorMessage>
                  {t("Please fill in your departure flight")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <FlightCombobox
                airportCode={searchResult.location.value}
                label={t("Arriving Flight") + " *"}
                id="arrivalFlight"
                direction={FLIGHT_DIRECTION.ARRIVAL}
                name={arrivalFlightField.field.name}
                onValueChange={arrivalFlightField.field.onChange}
                error={Boolean(arrivalFlightField.fieldState.error)}
                dateTime={searchResult.departureDateTime}
                inputRef={arrivalFlightField.field.ref}
              />
              {errors.arrivalFlight && (
                <ErrorMessage>
                  {t("Please fill in your arrival flight")}
                </ErrorMessage>
              )}
            </div>
          </div>

          <h2 className="mb-2 mt-6 text-lg font-semibold leading-9 lg:mt-9 lg:text-2xl">
            {t("Car details")}
          </h2>
          <div className="mt-1 grid grid-cols-2 items-start gap-3 lg:gap-x-5">
            <div>
              <FloatingInputGroup
                error={Boolean(vehicleRegistrationNumberField.fieldState.error)}
              >
                <FloatingLabel htmlFor="registrationNumber">
                  {t("Registration Number")} *
                </FloatingLabel>
                <FloatingInput
                  value={vehicleRegistrationNumberField.field.value}
                  name={vehicleRegistrationNumberField.field.name}
                  onValueChange={vehicleRegistrationNumberField.field.onChange}
                  id="registrationNumber"
                  ref={vehicleRegistrationNumberField.field.ref}
                />
                <FloatingInputClearButton
                  onClick={() =>
                    vehicleRegistrationNumberField.field.onChange("")
                  }
                />
              </FloatingInputGroup>
              {errors.vehicle?.registrationNumber && (
                <ErrorMessage>
                  {t("Please fill in the car registration number")}
                </ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup
                error={Boolean(vehicleMakeField.fieldState.error)}
              >
                <FloatingLabel htmlFor="vehicleMakeField">
                  {t("Car make")} *
                </FloatingLabel>
                <FloatingInput
                  value={vehicleMakeField.field.value}
                  name={vehicleMakeField.field.name}
                  onValueChange={vehicleMakeField.field.onChange}
                  id="vehicleMakeField"
                  ref={vehicleMakeField.field.ref}
                />
                <FloatingInputClearButton
                  onClick={() => vehicleMakeField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.vehicle?.make && (
                <ErrorMessage>{t("Please fill in the car make")}</ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup
                error={Boolean(vehicleModelField.fieldState.error)}
              >
                <FloatingLabel htmlFor="vehicleModelField">
                  {t("Car model")} *
                </FloatingLabel>
                <FloatingInput
                  value={vehicleModelField.field.value}
                  name={vehicleModelField.field.name}
                  onValueChange={vehicleModelField.field.onChange}
                  id="vehicleModelField"
                  ref={vehicleModelField.field.ref}
                />
                <FloatingInputClearButton
                  onClick={() => vehicleModelField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.vehicle?.model && (
                <ErrorMessage>{t("Please fill in the car model")}</ErrorMessage>
              )}
            </div>

            <div>
              <FloatingInputGroup
                error={Boolean(vehicleColorField.fieldState.error)}
              >
                <FloatingLabel htmlFor="vehicleColorField">
                  {t("Car color")} *
                </FloatingLabel>
                <FloatingInput
                  value={vehicleColorField.field.value}
                  name={vehicleColorField.field.name}
                  onValueChange={vehicleColorField.field.onChange}
                  id="vehicleColorField"
                  ref={vehicleColorField.field.ref}
                />
                <FloatingInputClearButton
                  onClick={() => vehicleColorField.field.onChange("")}
                />
              </FloatingInputGroup>
              {errors.vehicle?.color && (
                <ErrorMessage>{t("Please fill in the car color")}</ErrorMessage>
              )}
            </div>
          </div>

          <h2 className="mb-2 mt-6 text-lg font-semibold leading-9 lg:mt-9 lg:text-2xl">
            {t("Extra Information")}
          </h2>
          <div className="mt-1 grid grid-cols-2 gap-3">
            <Select
              label={t("Travelers")}
              icon={
                <TravelerSvg
                  className={cn({
                    "mt-1": !!passengerCountField.field.value,
                  })}
                />
              }
              value={passengerCountField.field.value || ""}
              name={passengerCountField.field.name}
              onValueChange={passengerCountField.field.onChange}
              options={Array.from({ length: 10 }, (_, index) => index + 1).map(
                (item) => item.toString(),
              )}
            />

            <Select
              label={t("Reasons for Travel")}
              icon={
                <ReasonSvg
                  className={cn({
                    "mt-1": !!passengerCountField.field.value,
                  })}
                />
              }
              options={["Business", "Vacation"]}
              value={travelReasonField.field.value || ""}
              name={travelReasonField.field.name}
              onValueChange={travelReasonField.field.onChange}
            />
          </div>
        </div>
        <div>
          <div className="hidden lg:block">{renderPurchaseSummary()}</div>

          <div className="mt-3 border-y border-y-neutral-200 bg-white px-3 py-3 lg:mt-0 lg:border-y-0 lg:bg-transparent lg:p-0">
            <FloatingInputGroup
              success={Boolean(pricingResult?.couponCode)}
              error={pricingResult?.invalidCoupon}
            >
              <FloatingLabel htmlFor="couponCode">
                {t("Coupon Code")}
              </FloatingLabel>
              <FloatingInput
                value={couponCodeField.field.value}
                name={couponCodeField.field.name}
                onValueChange={couponCodeField.field.onChange}
                onBlur={(event) => onCouponCodeChange?.(event.target.value)}
                id="couponCode"
              />
              <FloatingInputClearButton
                onClick={() => {
                  couponCodeField.field.onChange("");
                  onCouponCodeChange?.("");
                }}
              />
            </FloatingInputGroup>

            {pricingResult?.invalidCoupon && (
              <p className="text-error pt-1 text-xs">
                {t(
                  "The coupon code is invalid, has expired or reached the limit of uses.",
                )}
              </p>
            )}

            {pricingResult?.couponCode && (
              <div className="hidden items-center justify-between pt-4 font-bold lg:flex">
                <p>{pricingResult.couponCode}</p>
                <p>-{pricingResult.couponDiscount.display}</p>
              </div>
            )}

            <div className="hidden items-start justify-between pb-2 pt-5 lg:flex">
              <p className="text-2xl font-bold">{t("Total Price")}</p>
              <div className="text-right">
                <span className="flex justify-end font-semibold lg:text-2xl">
                  {pricingResult?.finalPrice.display ?? (
                    <Loading className="text-[2rem]" />
                  )}
                </span>
                <p className="mt-1 text-xs text-neutral-800">
                  {t("Taxes and Fees included")}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 border-y border-y-neutral-200 bg-white px-3 py-3 lg:rounded-lg lg:border lg:p-5">
            {/* TODO: Check how to handle or if we need it */}
            {/* <LinkAuthenticationElement onChange={(e) => console.log(e.value)} /> */}
            {pricingResult &&
              pricingResult.finalPrice.smallestCurrencyUnit > 0 && (
                <>
                  <h2 className="mb-2 text-lg font-semibold leading-9 lg:text-2xl">
                    {t("Payment details")}
                  </h2>
                  <PaymentElement />
                </>
              )}
            <Button
              type="submit"
              disabled={loading || !pricingResult}
              className="my-3 w-full text-base"
            >
              {t("Book Now")}
            </Button>
            <p className="text-xs">
              By pressing the book now button you agree with our{" "}
              <Link
                to="/terms-and-conditions"
                className="underline"
                target="_blank"
              >
                Terms and Conditions
              </Link>
            </p>
          </div>
        </div>

        {pricingResult && (
          <PriceSummary
            finalPrice={pricingResult.finalPrice?.display}
            reservationPrice={pricingResult.originalPrice.display}
            couponCode={pricingResult.couponCode}
            couponDiscount={pricingResult.couponDiscount.display}
          />
        )}
      </form>
    </>
  );
};

const ErrorMessage = (props: JSX.IntrinsicElements["p"]) => {
  return <p className="mt-0.5 text-xs text-red-500" role="alert" {...props} />;
};

export { BookingForm };
